import {
  MdGroups,
  MdTrendingUp,
  MdOutlineWarningAmber,
  MdOutlineBlock,
  MdBedtime,
} from "react-icons/md";
import { formatCompactMoney, formatNumber } from "./customerHelpers";

const KpiCard = ({
  icon: Icon,
  label,
  value,
  hint,
  accent = "text-slate-900",
  iconClass = "bg-slate-100 text-slate-500",
  isActive = false,
  onClick,
}) => {
  const isClickable = typeof onClick === "function";

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!isClickable}
      className={`flex w-full items-start gap-3 rounded-2xl border bg-white p-4 text-left shadow-sm transition ${
        isActive
          ? "border-pink-300 ring-2 ring-pink-100"
          : "border-slate-200"
      } ${isClickable ? "hover:border-pink-200 hover:shadow-md" : "cursor-default"}`}
    >
      <span className={`rounded-xl p-2 ${iconClass}`}>
        <Icon size={20} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
          {label}
        </span>
        <span className={`block text-2xl font-bold leading-tight ${accent}`}>
          {value}
        </span>
        {hint ? (
          <span className="block truncate text-xs text-slate-500">{hint}</span>
        ) : null}
      </span>
    </button>
  );
};

const SkeletonCard = () => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
    <div className="h-3 w-24 animate-pulse rounded-full bg-slate-200" />
    <div className="mt-3 h-7 w-16 animate-pulse rounded-lg bg-slate-200" />
    <div className="mt-2 h-3 w-28 animate-pulse rounded-full bg-slate-100" />
  </div>
);

/**
 * Faixa de indicadores no topo da tela. Cada card também funciona como atalho
 * de filtro — clicar em "Expirando" filtra a tabela por esse recorte.
 */
const CustomerKpiCards = ({ summary, isLoading, activeStatus, activeHealth, onSelect }) => {
  if (isLoading) {
    return (
      <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
        {Array.from({ length: 5 }).map((_, index) => (
          <SkeletonCard key={`kpi-skeleton-${index}`} />
        ))}
      </section>
    );
  }

  const status = summary?.status ?? {};
  const health = summary?.health ?? {};

  return (
    <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
      <KpiCard
        icon={MdGroups}
        label="Clientes"
        value={formatNumber(summary?.total)}
        hint={`${formatNumber(summary?.newThisMonth)} novos neste mês`}
        iconClass="bg-slate-100 text-slate-600"
        isActive={!activeStatus && !activeHealth}
        onClick={() => onSelect({ status: "", health: "" })}
      />
      <KpiCard
        icon={MdTrendingUp}
        label="Ativos"
        value={formatNumber(status.ACTIVE)}
        hint={`${summary?.engagementRate ?? 0}% usando o sistema`}
        accent="text-emerald-600"
        iconClass="bg-emerald-50 text-emerald-600"
        isActive={activeStatus === "ACTIVE"}
        onClick={() => onSelect({ status: "ACTIVE", health: "" })}
      />
      <KpiCard
        icon={MdOutlineWarningAmber}
        label="Expirando (2d)"
        value={formatNumber(status.EXPIRING)}
        hint="Renovação a vencer"
        accent="text-amber-600"
        iconClass="bg-amber-50 text-amber-600"
        isActive={activeStatus === "EXPIRING"}
        onClick={() => onSelect({ status: "EXPIRING", health: "" })}
      />
      <KpiCard
        icon={MdOutlineBlock}
        label="Expirados"
        value={formatNumber(status.EXPIRED)}
        hint={`${formatCompactMoney(summary?.churnRisk)} em risco/mês`}
        accent="text-rose-600"
        iconClass="bg-rose-50 text-rose-600"
        isActive={activeStatus === "EXPIRED"}
        onClick={() => onSelect({ status: "EXPIRED", health: "" })}
      />
      <KpiCard
        icon={MdBedtime}
        label="Sem uso"
        value={formatNumber(health.NEVER_USED)}
        hint={`${formatNumber(health.AT_RISK)} pararam de usar`}
        accent="text-slate-700"
        iconClass="bg-violet-50 text-violet-600"
        isActive={activeHealth === "NEVER_USED"}
        onClick={() => onSelect({ status: "", health: "NEVER_USED" })}
      />
    </section>
  );
};

export default CustomerKpiCards;
